import { headerSlotInsetOptions } from '@/components/ui/pageFrame';
import { Stack } from 'expo-router';
import { getTokenValue, YStack } from 'tamagui';
import { StatusBar } from 'expo-status-bar';
import { Platform } from 'react-native';

import PSToastHost from '@/components/ui/PSToastHost';
import { AuthContextProvider } from '@/src/lib/authContext';

/**
 * Auth flow layout — the nested Stack for the `/auth/*` modal group.
 *
 * The whole flow is presented as a sheet over Home (Airbnb-style): the
 * `presentation: 'modal'` that slides it up is declared on the PARENT stack
 * (the `<Stack.Screen name="auth">` entry in app/_layout.tsx) — a nested Stack
 * can't make itself modal. Here we own the flow's INTERNAL stack: each step
 * pushes onto it left-to-right (`animation: 'slide_from_right'`).
 *
 * Signup itself is only THREE steps — mobile, verify-mobile, details (name) —
 * and email is no longer part of it: `details` ends the flow by closing the
 * modal once a name is saved. `email` and `verify-email` are reached later,
 * as a separate booking-time detour (pushed directly, bypassing the first
 * three screens) once a session tries to book without a verified email — see
 * `details.tsx` and `email.tsx`'s docstrings.
 *
 * There is NO close button and NO swipe-to-dismiss gesture (`gestureEnabled:
 * false` here, applied to every step) — we want to disincentivize abandoning a
 * half-finished signup. The only backward affordance is a ← back button on the
 * two verify screens (returning to the step that owns the field — i.e. "change
 * number" / "change email"); the entry screens (mobile, details, email) have NO
 * headerLeft at all, so the flow only moves forward from them. Each screen sets
 * its own `headerLeft` (a ← back, or none); this layout owns the shared chrome.
 *
 * `AuthDraftProvider` is mounted here (not at the root) so the in-progress
 * mobile/email/name is scoped to the flow and discarded when it unmounts — see
 * [[authDraft]]. The long-lived session ([[session]]) lives at the root instead,
 * so Home can read it after the flow ends.
 *
 * `PSToastHost` is mounted here for a related but different reason: it has to be
 * INSIDE the modal to be seen at all. On iOS the flow is a natively-presented
 * sheet, and the root layout's host would render underneath it. It sits as a
 * sibling of the Stack (absolutely positioned over it) so every step in the flow
 * shares one host. The root host un-mounts itself while this group is the active
 * route, so the two never draw the same toast — see [[PSToastHost]].
 */
export default function AuthLayout() {
  return (
    <AuthContextProvider>
      <YStack flex={1}>
        <Stack
          screenOptions={{
            ...headerSlotInsetOptions,
            headerShown: true,
            headerShadowVisible: false,
            headerStyle: {
              backgroundColor: getTokenValue('$color.background'),
            },
            contentStyle: {
              backgroundColor: getTokenValue('$color.background'),
            },
            headerTitle: () => <></>,
            animation: 'slide_from_right',
            // No swipe-to-dismiss on any step — see the module docstring.
            gestureEnabled: false,
          }}
        />
        <PSToastHost />
        <StatusBar style={Platform.select({ android: 'dark', ios: 'light' })} />
      </YStack>
    </AuthContextProvider>
  );
}
